import { Button, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { VisibilityOutlined } from '@mui/icons-material';
import { FC, useState } from 'react';
import { IAd } from '../../interfaces/tasks.ts';
import { formatNumberWithComma } from '../../utils/number.ts';
import imgPlaceholder from '../../assets/image-placeholder.svg';
import { VIDEO_TYPES } from '../../constants/array.ts';
import AdRejectDialog from './AdRejectDialog.tsx';
import axios from 'axios';
import IResponse from '../../interfaces/response.ts';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';

interface ICardPendingAds {
	card: IAd;
	refetch: () => void;
}

const CardPendingAds: FC<ICardPendingAds> = ({ card, refetch }) => {
	const [openRejectModal, setOpenRejectModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const { t } = useTranslation();
	const navigate = useNavigate();

	const formattedPrice = formatNumberWithComma(card?.price);

	const isVideo = VIDEO_TYPES.some(type => card?.image?.includes(type));

	const handleCloseRejectModal = () => {
		setOpenRejectModal(false);
	};

	const handleOpenRejectModal = () => {
		setOpenRejectModal(true);
	};

	const hanleRejectAd = async (title: string, description: string) => {
		setSubmitting(true);
		axios<IResponse<{ data: boolean }>>({
			url: `/duties/reject/${card?.id}`,
			method: 'POST',
			data: {
				title,
				description,
			},
		})
			.then(res => {
				if (res.data.success) {
					console.log(res.data.data);
				}
			})
			.finally(() => {
				setSubmitting(false);
				refetch();
				handleCloseRejectModal();
			});
	};

	const handleConfirmAd = async () => {
		setSubmitting(true);
		axios<IResponse<{ data: boolean }>>({
			url: `/duties/confirm/${card?.id}`,
			method: 'POST',
		})
			.then(res => {
				if (res.data.success) {
					console.log(res.data.data);
				}
			})
			.finally(() => {
				setSubmitting(false);
				refetch();
			});
	};

	const handleMoreDetails = () => {
		navigate(`/my-tasks/details/${card?.id}`);
	};

	return (
		<div className="w-full p-4 md:w-1/2 lg:w-1/3">
			<div className="w-full rounded-lg border border-primary-outborder bg-white p-4 shadow-md">
				{!isVideo && <img src={card?.image || imgPlaceholder} alt="Ad preview picture" className="mb-4 max-h-56 min-h-56 w-full rounded-md" />}

				{isVideo && (
					<video controls className="mb-4 max-h-56 min-h-56 w-full rounded-md">
						<source src={card?.image!} type="video/mp4" />
						{t('your_browser_does_not_support_the_video_tag')}
					</video>
				)}

				<Button variant="outlined" className="mb-4 w-full" size="small" startIcon={<VisibilityOutlined />} onClick={handleMoreDetails}>
					{t('more_details')}
				</Button>
				<div className="flex flex-col">
					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('category')}:</Typography>
						<Typography className="text-sm text-text-primary">{card?.category_title}</Typography>
					</div>

					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('content_type')}:</Typography>
						<Typography className="text-sm text-text-primary">{t(card?.content_type)}</Typography>
					</div>

					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('release-platform')}:</Typography>
						<Typography className="text-sm text-text-primary">{t(card?.media)}</Typography>
					</div>

					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('insert_date')}:</Typography>
						<Typography className="text-sm text-text-primary">{card?.date ? new Date(card?.date).toLocaleDateString('fa-IR') : ''}</Typography>
					</div>

					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('advertiser_page')}:</Typography>
						<Typography className="text-sm text-text-primary">{card?.handle_name}@</Typography>
					</div>

					<div className="mb-2 flex justify-between">
						<Typography className="text-sm text-text-secondary/60">{t('cost')}:</Typography>
						<Trans i18nKey="tooman_amount_label" values={{ amount: formattedPrice }} components={[<Typography className="text-sm font-medium text-text-primary" />]} />
					</div>
				</div>
				<div className="mt-4 flex gap-2">
					<LoadingButton loading={submitting} variant="contained" size="small" className="w-2/3 text-sm sm:w-60" onClick={handleConfirmAd}>
						{submitting ? '' : t('confirm')}
					</LoadingButton>
					<Button variant="outlined" size="small" className="w-1/3 text-sm sm:w-28" onClick={handleOpenRejectModal}>
						{t('refuse')}
					</Button>
				</div>
			</div>
			<AdRejectDialog submitting={submitting} open={openRejectModal} onClose={handleCloseRejectModal} onSubmit={hanleRejectAd} />
		</div>
	);
};

export default CardPendingAds;
